import { supabase } from '@/lib/supabase';

// Torre: estrutura física, geração de unidades e persistência dos andares.
//
// A regra de nomenclatura e a conta de quantas unidades saem de quantos
// pavimentos vivem aqui, e não no componente: são as duas coisas que a tela
// precisa mostrar antes de gerar e que o banco precisa produzir igual depois
// (migration 0053). Duas implementações da mesma regra divergiriam justamente
// na prévia — a tela prometendo 101 e o banco gravando 1.

export interface Andar {
  andar: number;
  unidades: number;
  descricao: string;
}

export interface Torre {
  id: string;
  condominio_id: string;
  nome: string;
  pavimentos: number;
  aptos_por_andar: number;
  area_construida: number | null;
  ativo: boolean;
  andares: Andar[] | null;
  pavimentos_com_unidades: number | null;
  primeiro_pavimento_com_unidades: number;
  unidades_geradas_em: string | null;
}

export interface EstruturaDaTorre {
  /** Total de pavimentos, com ou sem unidades. */
  pavimentos: number;
  /** Primeiro pavimento que tem unidades — 2 quando o térreo é garagem. */
  primeiroPavimento: number;
  /** Quantos pavimentos, a partir do primeiro, têm unidades. */
  pavimentosComUnidades: number;
  /** Unidades em cada um desses pavimentos. */
  unidadesPorPavimento: number;
}

export interface UnidadeDeTorre {
  id: string;
  unidade: string;
  andar: string | null;
  tipo: string | null;
  proprietario: string | null;
  locatario: string | null;
  status: string | null;
  ativo: boolean;
}

// Literal de uma linha só: o cliente tipado do Supabase infere a forma do
// retorno a partir do texto do select, e uma concatenação em runtime o deixa
// sem nada para inferir.
export const SELECT_TORRE =
  'id, condominio_id, nome, pavimentos, aptos_por_andar, area_construida, ativo, andares, pavimentos_com_unidades, primeiro_pavimento_com_unidades, unidades_geradas_em' as const;

/**
 * Nome de uma unidade: pavimento + sequência de dois dígitos.
 *
 * 101 no primeiro pavimento, 1204 no décimo segundo. Espelha exatamente o que
 * `gerar_unidades_da_torre` grava; mudar um lado sem o outro faz a prévia
 * mentir.
 */
export function nomeDaUnidade(pavimento: number, sequencia: number): string {
  return `${pavimento}${String(sequencia).padStart(2, '0')}`;
}

/** Todos os nomes que a geração vai criar, na ordem em que serão criados. */
export function nomesDasUnidades(estrutura: EstruturaDaTorre): string[] {
  const { primeiroPavimento, pavimentosComUnidades, unidadesPorPavimento } = estrutura;
  if (pavimentosComUnidades < 1 || unidadesPorPavimento < 1 || primeiroPavimento < 1) return [];

  const nomes: string[] = [];
  for (let p = primeiroPavimento; p < primeiroPavimento + pavimentosComUnidades; p++) {
    for (let s = 1; s <= unidadesPorPavimento; s++) {
      nomes.push(nomeDaUnidade(p, s));
    }
  }
  return nomes;
}

export interface ProblemaDaEstrutura {
  campo: keyof EstruturaDaTorre;
  mensagem: string;
}

/**
 * O que impede a geração. Lista vazia = pode gerar.
 *
 * Mesmas checagens da função do banco, aqui só para explicar antes de tentar —
 * quem recusa de verdade é a migration 0053.
 */
export function validarEstrutura(estrutura: EstruturaDaTorre): ProblemaDaEstrutura[] {
  const problemas: ProblemaDaEstrutura[] = [];
  const { pavimentos, primeiroPavimento, pavimentosComUnidades, unidadesPorPavimento } = estrutura;

  if (pavimentos < 1) {
    problemas.push({ campo: 'pavimentos', mensagem: 'A torre precisa ter ao menos um pavimento.' });
  }
  if (primeiroPavimento < 1) {
    problemas.push({
      campo: 'primeiroPavimento',
      mensagem: 'O primeiro pavimento com unidades começa em 1.',
    });
  }
  if (pavimentosComUnidades < 1) {
    problemas.push({
      campo: 'pavimentosComUnidades',
      mensagem: 'Informe quantos pavimentos têm unidades.',
    });
  }
  if (pavimentosComUnidades > pavimentos) {
    problemas.push({
      campo: 'pavimentosComUnidades',
      mensagem: `A torre tem ${pavimentos} pavimento(s): não há como ${pavimentosComUnidades} terem unidades.`,
    });
  }
  if (unidadesPorPavimento < 1) {
    problemas.push({
      campo: 'unidadesPorPavimento',
      mensagem: 'Informe quantas unidades existem em cada pavimento.',
    });
  }
  // O último pavimento com unidade não pode passar do topo do prédio.
  if (
    pavimentos >= 1 &&
    primeiroPavimento >= 1 &&
    pavimentosComUnidades >= 1 &&
    primeiroPavimento + pavimentosComUnidades - 1 > pavimentos
  ) {
    problemas.push({
      campo: 'primeiroPavimento',
      mensagem: `Começando no pavimento ${primeiroPavimento}, ${pavimentosComUnidades} pavimento(s) com unidades passam do ${pavimentos}º.`,
    });
  }

  return problemas;
}

export interface ResumoDaGeracao {
  total: number;
  primeira: string;
  ultima: string;
  /** Amostra para a confirmação: as primeiras e as últimas, sem despejar tudo. */
  amostra: string[];
  truncada: boolean;
}

export function resumirGeracao(estrutura: EstruturaDaTorre): ResumoDaGeracao | null {
  const nomes = nomesDasUnidades(estrutura);
  if (nomes.length === 0) return null;

  const LIMITE = 12;
  const truncada = nomes.length > LIMITE;
  return {
    total: nomes.length,
    primeira: nomes[0],
    ultima: nomes[nomes.length - 1],
    amostra: truncada ? [...nomes.slice(0, 8), ...nomes.slice(-2)] : nomes,
    truncada,
  };
}

/** Os andares que a geração vai registrar na configuração da torre. */
export function andaresDaEstrutura(estrutura: EstruturaDaTorre): Andar[] {
  const andares: Andar[] = [];
  for (
    let p = estrutura.primeiroPavimento;
    p < estrutura.primeiroPavimento + estrutura.pavimentosComUnidades;
    p++
  ) {
    andares.push({ andar: p, unidades: estrutura.unidadesPorPavimento, descricao: '' });
  }
  return andares;
}

/** Já gerou uma vez: não gera de novo, aqui nem no banco. */
export function jaGerouUnidades(torre: Pick<Torre, 'unidades_geradas_em'>): boolean {
  return !!torre.unidades_geradas_em;
}

// ─── Persistência ────────────────────────────────────────────────────

export interface TorreParaCriar {
  condominioId: string;
  nome: string;
  pavimentos: number;
  unidadesPorPavimento: number;
  areaConstruida: number | null;
  primeiroPavimento: number;
  pavimentosComUnidades: number | null;
}

export async function criarTorre(dados: TorreParaCriar): Promise<Torre> {
  const { data, error } = await supabase
    .from('torres')
    .insert({
      condominio_id: dados.condominioId,
      nome: dados.nome.trim(),
      pavimentos: dados.pavimentos,
      aptos_por_andar: dados.unidadesPorPavimento,
      area_construida: dados.areaConstruida || null,
      primeiro_pavimento_com_unidades: dados.primeiroPavimento,
      pavimentos_com_unidades: dados.pavimentosComUnidades,
      ativo: true,
    })
    .select(SELECT_TORRE)
    .single();

  if (error) throw new Error(error.message);
  return data as Torre;
}

export interface TorreParaSalvar {
  nome: string;
  pavimentos: number;
  unidadesPorPavimento: number;
  areaConstruida: number | null;
  primeiroPavimento: number;
  pavimentosComUnidades: number | null;
  andares: Andar[];
}

/**
 * Grava a torre inteira — características e andares.
 *
 * A tela de configuração mantinha as duas coisas só no estado do React: o
 * "Salvar Torre" mostrava um toast e o andar adicionado sumia no F5 (P-34).
 */
export async function salvarTorre(torreId: string, dados: TorreParaSalvar): Promise<void> {
  const { error } = await supabase
    .from('torres')
    .update({
      nome: dados.nome.trim(),
      pavimentos: dados.pavimentos,
      aptos_por_andar: dados.unidadesPorPavimento,
      area_construida: dados.areaConstruida,
      primeiro_pavimento_com_unidades: dados.primeiroPavimento,
      pavimentos_com_unidades: dados.pavimentosComUnidades,
      andares: [...dados.andares].sort((a, b) => a.andar - b.andar),
    })
    .eq('id', torreId);

  if (error) throw new Error(error.message);
}

/** Só os andares — usado quando a tela grava a lista sem tocar no cabeçalho. */
export async function salvarAndares(torreId: string, andares: Andar[]): Promise<void> {
  const { error } = await supabase
    .from('torres')
    .update({ andares: [...andares].sort((a, b) => a.andar - b.andar) })
    .eq('id', torreId);

  if (error) throw new Error(error.message);
}

export interface UnidadesGeradas {
  torre_id: string;
  criadas: number;
  primeira: string;
  ultima: string;
}

/**
 * Gera as unidades da torre. Uma vez por torre — a recusa é do banco.
 *
 * O erro sobe cru de propósito: `interpretarErroDeLimite` precisa do `hint` e
 * do `details` originais para traduzir o estouro de plano em convite de
 * upgrade em vez de mensagem de banco.
 */
export async function gerarUnidades(
  torreId: string,
  estrutura: EstruturaDaTorre,
): Promise<UnidadesGeradas> {
  const { data, error } = await supabase.rpc('gerar_unidades_da_torre', {
    p_torre_id: torreId,
    p_primeiro_pavimento: estrutura.primeiroPavimento,
    p_pavimentos_com_unidades: estrutura.pavimentosComUnidades,
    p_unidades_por_pavimento: estrutura.unidadesPorPavimento,
  });

  if (error) throw error;
  return data as UnidadesGeradas;
}

export async function lerUnidadesDaTorre(torreId: string): Promise<UnidadeDeTorre[]> {
  const { data, error } = await supabase
    .from('imoveis')
    .select('id, unidade, andar, tipo, proprietario, locatario, status, ativo')
    .eq('torre_id', torreId)
    .order('unidade', { ascending: true });

  if (error) throw new Error(error.message);
  return (data || []) as UnidadeDeTorre[];
}

/**
 * Vincula à torre os equipamentos escolhidos no cadastro.
 *
 * Cada escolha vira uma linha de `infraestrutura` com `torre_id` — o mesmo
 * registro que a aba Infraestrutura mantém, e não uma lista paralela. Categoria
 * e periodicidade vêm do catálogo; o gatilho da migration 0049 cuida do resto.
 */
export async function vincularEquipamentos(
  condominioId: string,
  torreId: string,
  itens: Array<{ id: string; nome: string; categoria_id: string }>,
): Promise<void> {
  if (itens.length === 0) return;

  const { error } = await supabase.from('infraestrutura').insert(
    itens.map((item) => ({
      condominio_id: condominioId,
      torre_id: torreId,
      nome: item.nome,
      categoria_id: item.categoria_id || null,
      item_catalogo_id: item.id,
      status: 'Operacional',
      cadastro_situacao: 'em_andamento',
    })),
  );

  if (error) throw new Error(error.message);
}
